import { useState } from "react";
import { ChevronsUpDown, Check, X, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { isCustomOption } from "@/lib/builderOptions";

const clamp = (s, n) => (s || "").slice(0, n);

export function ComboField({ label, optional, value, onChange, options, customLabel, customMax = 80, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const isCustom = !!value && !options.includes(value);
  const showCustom = customOpen || isCustom;

  const pick = (opt) => {
    if (opt === customLabel) {
      setCustomOpen(true);
      onChange("");
    } else {
      setCustomOpen(false);
      onChange(opt);
    }
    setOpen(false);
  };

  return (
    <div id={`field-${testid}`} className="w-full">
      <label className="mb-1.5 block text-sm font-bold text-[#4A235A]">
        {label} {optional && <span className="font-medium text-[#5C4A61]">— optional</span>}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`combo-${testid}`}
            aria-label={label}
            className="flex min-h-[52px] w-full items-center justify-between gap-2 rounded-2xl border border-[#4A235A]/15 bg-white px-4 text-left text-base font-medium text-[#1A0F1C] outline-none transition-colors hover:border-[#9B5DE5] focus:ring-4 focus:ring-[#F4D03F]/40"
          >
            <span className={value || showCustom ? "" : "text-[#9A8AA0]"}>
              {showCustom ? (value || "Custom answer") : value || placeholder}
            </span>
            <span className="flex items-center gap-1">
              {(value || showCustom) && (
                <X
                  size={16}
                  className="text-[#9A8AA0] hover:text-[#4A235A]"
                  onClick={(e) => { e.stopPropagation(); onChange(""); setCustomOpen(false); }}
                />
              )}
              <ChevronsUpDown size={16} className="text-[#9A8AA0]" />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0" data-testid={`combo-content-${testid}`}>
          <Command>
            <CommandInput placeholder="Search…" />
            <CommandList>
              <CommandEmpty>No match. Try Custom answer.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt}
                    value={opt}
                    onSelect={() => pick(opt)}
                    data-testid={`option-${testid}-${opt.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                    className="min-h-[44px] cursor-pointer"
                  >
                    <Check size={16} className={value === opt ? "opacity-100 text-[#9B5DE5]" : "opacity-0"} />
                    {opt}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {showCustom && (
        <input
          autoFocus
          value={isCustom ? value : ""}
          maxLength={customMax}
          onChange={(e) => onChange(clamp(e.target.value, customMax))}
          placeholder={`Type your own (${customMax} chars max)`}
          data-testid={`custom-${testid}`}
          className="mt-2 w-full rounded-2xl border border-[#9B5DE5]/50 bg-white p-3.5 text-base font-medium outline-none focus:ring-4 focus:ring-[#F4D03F]/40"
        />
      )}
    </div>
  );
}

export function MultiField({ label, value, onChange, options, max, customLabel, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const arr = Array.isArray(value) ? value : [];
  const atMax = arr.length >= max;

  const toggle = (opt) => {
    if (opt === customLabel) {
      setCustomOpen(true);
      return;
    }
    if (arr.includes(opt)) {
      onChange(arr.filter((v) => v !== opt));
    } else if (!atMax) {
      onChange([...arr, opt]);
    }
  };

  const addCustom = () => {
    const t = clamp(draft.trim(), 60);
    if (t && !arr.includes(t) && !atMax) {
      onChange([...arr, t]);
      setDraft("");
      setCustomOpen(false);
    }
  };

  return (
    <div id={`field-${testid}`} className="w-full">
      <label className="mb-1.5 block text-sm font-bold text-[#4A235A]">
        {label} <span className="font-medium text-[#5C4A61]">— up to {max}</span>
      </label>

      {arr.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2" data-testid={`chips-${testid}`}>
          {arr.map((chip) => (
            <span key={chip} className="inline-flex items-center gap-1 rounded-full bg-[#4A235A] px-3 py-1.5 text-sm font-semibold text-white">
              {chip}
              <X size={14} className="cursor-pointer opacity-80 hover:opacity-100"
                 onClick={() => onChange(arr.filter((v) => v !== chip))}
                 data-testid={`chip-remove-${testid}-${chip.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`} />
            </span>
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`combo-${testid}`}
            aria-label={label}
            className="flex min-h-[52px] w-full items-center justify-between gap-2 rounded-2xl border border-[#4A235A]/15 bg-white px-4 text-left text-base font-medium outline-none transition-colors hover:border-[#9B5DE5] focus:ring-4 focus:ring-[#F4D03F]/40"
          >
            <span className={arr.length ? "" : "text-[#9A8AA0]"}>
              {arr.length ? `${arr.length} selected` : placeholder}
            </span>
            <ChevronsUpDown size={16} className="text-[#9A8AA0]" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0" data-testid={`combo-content-${testid}`}>
          <Command>
            <CommandInput placeholder="Search…" />
            <CommandList>
              <CommandEmpty>No match. Try Custom answer.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => {
                  const selected = arr.includes(opt);
                  const disabled = !selected && atMax && !isCustomOption(opt);
                  return (
                    <CommandItem
                      key={opt}
                      value={opt}
                      onSelect={() => toggle(opt)}
                      disabled={disabled}
                      data-testid={`option-${testid}-${opt.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                      className="min-h-[44px] cursor-pointer"
                    >
                      <Check size={16} className={selected ? "opacity-100 text-[#9B5DE5]" : "opacity-0"} />
                      {opt}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {atMax && <p className="mt-1 text-xs font-semibold text-[#9B5DE5]">Maximum {max} reached — remove one to add another.</p>}

      {customOpen && !atMax && (
        <div className="mt-2 flex gap-2">
          <input
            autoFocus
            value={draft}
            maxLength={60}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
            placeholder="Type your own, then Add"
            data-testid={`custom-${testid}`}
            className="w-full rounded-2xl border border-[#9B5DE5]/50 bg-white p-3.5 text-base font-medium outline-none focus:ring-4 focus:ring-[#F4D03F]/40"
          />
          <button
            type="button"
            onClick={addCustom}
            data-testid={`custom-add-${testid}`}
            className="inline-flex items-center gap-1 rounded-2xl bg-[#9B5DE5] px-4 font-bold text-white"
          >
            <Plus size={16} /> Add
          </button>
        </div>
      )}
    </div>
  );
}
